import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import PageTransition from '@/components/PageTransition';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, formData, {
        withCredentials: true,
      });
      toast.success(t('auth.registerSuccess'));
      navigate('/dashboard', { state: { user: response.data.user } });
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = t('errors.submissionError');
      
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        errorMsg = detail[0].msg;
      }
      
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <PageTransition>
      <div className={`min-h-screen flex relative z-10 ${isDark ? '' : 'bg-white'}`}>
        {/* Decorative Side */}
        <div className={`hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden ${
          isDark ? '' : 'bg-gray-50'
        }`}>
          <div className="absolute inset-0 bg-gradient-to-bl from-pink-600/20 via-violet-600/10 to-transparent"></div>
          <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-pink-500/20 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-violet-500/20 rounded-full filter blur-3xl"></div>
          
          <div className="max-w-md text-center relative z-10">
            <h2 className={`text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('auth.joinSimblyo')}
            </h2>
            <p className={`text-lg mb-8 ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
              {t('auth.entrepreneursTrust')}
            </p>
            <div className="space-y-4 text-left">
              {[t('auth.features.freeTrial'), t('auth.features.quickSetup'), t('auth.features.prioritySupport')].map((feature, i) => (
                <div key={i} className={`flex items-center gap-3 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    isDark ? 'bg-pink-500/20' : 'bg-pink-100'
                  }`}>
                    <Check size={14} weight="bold" className="text-pink-500" />
                  </div>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form Side */}
        <div className="flex-1 flex items-center justify-center p-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="w-full max-w-md"
          >
            <div className="flex justify-end mb-6">
              <ThemeToggle />
            </div>
            
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-lg shadow-violet-500/25">
                  <Sparkle size={20} weight="fill" className="text-white" />
                </div>
                <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>SIMBLYO</span>
              </Link>
              <p className={`${isDark ? 'text-white/50' : 'text-gray-600'}`}>
                {t('auth.createAccountFree')}
              </p>
            </div>

            <div className={`p-8 space-y-6 rounded-2xl ${
              isDark 
                ? 'glass-card' 
                : 'bg-white border border-gray-200 shadow-lg'
            }`}>
              {/* Error Alert */}
              {error && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm" data-testid="register-error">
                  <p className="font-medium">{error}</p>
                  {error.includes('email') && (error.includes('utilisé') || error.includes('registered')) && (
                    <p className="mt-1 text-red-400/80">
                      <Link to="/login" className="underline hover:text-red-400">{t('auth.loginHere')}</Link> {t('auth.haveAccount').toLowerCase().replace('?', '').trim()}.
                    </p>
                  )}
                </div>
              )}
              
              <Button
                type="button"
                variant="outline"
                className={`w-full transition-all ${
                  isDark 
                    ? 'bg-white/[0.03] border-white/[0.1] hover:bg-white/[0.06]' 
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
                onClick={handleGoogleRegister}
                data-testid="google-register-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.googleLogin')}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className={`w-full border-t ${isDark ? 'border-white/[0.08]' : 'border-gray-200'}`} />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className={`px-3 ${isDark ? 'bg-[hsl(230,22%,16%)] text-white/40' : 'bg-white text-gray-500'}`}>
                    {t('common.or')}
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className={isDark ? 'text-white/70' : 'text-gray-700'}>{t('auth.fullName')}</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Jean Dupont"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className={isDark ? 'text-white/70' : 'text-gray-700'}>{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className={isDark ? 'text-white/70' : 'text-gray-700'}>{t('auth.password')}</Label>
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="register-password-input"
                  />
                  <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    {t('auth.minChars')}
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-premium py-6" 
                  disabled={loading} 
                  data-testid="register-submit-btn"
                >
                  {loading ? t('auth.creating') : t('auth.createAccount')}
                </Button>
              </form>

              <p className={`text-center text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                {t('auth.haveAccount')}{' '}
                <Link to="/login" className="text-violet-500 hover:text-violet-400 transition-colors font-medium">
                  {t('auth.login')}
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </PageTransition>
  );
}
